/**
 * Header search — the global backend search palette (⌘K / Ctrl+K).
 *
 * The field is a button styled like an input; the real surface is the shared
 * `@authe/search` `SearchCommand`, driven by the `search-mgmt-api` provider
 * (`useSearchProvider`). It searches transactions, addresses, catalog
 * (entities/tags/labels/groups) and tenancy (users/tenants/orgs) server-side,
 * with enrichment (chains, risk, labels) rendered via `ui-core`. Recents are
 * scoped to the caller's identity. Page navigation lives in the sidebar.
 */
import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { ShortcutManager } from "@authe/fe-commons/shortcuts";
import type { StorageIdentity } from "@authe/fe-commons/storage";
import { toStoredChainId } from "@authe/fe-commons/chains";
import { useSession } from "@authe/ui-core/session";
import { useCurrentOrg } from "@authe/ui-core/tenant";
import { SearchCommand, type SearchHit } from "@authe/search";
import { useSearchProvider } from "@/api/adapters";
import { APPLICATION_SLUG } from "@/config";

/**
 * Build the `/transactions/$id` route param for a transaction hit.
 *
 * The search backend emits one hit per chain with a route-ready id: a
 * content-addressed `canonical_tx_id` (bare 64-hex) for indexed txs, or the
 * chain-scoped `<chain>:<hash>` form for not-yet-indexed ones. Both route as-is
 * — a value already carrying a `:` is chain-scoped, and a bare-hex id is
 * canonical. Only a bare raw hash (`0x…`, e.g. from a custom provider) is
 * ambiguous across forked chains, so we prefix it with the hit's chain.
 *
 * The prefix is the canonical CAIP-2 id, which itself contains a colon
 * (`eip155:1:0xabc…`). That is unambiguous to the API, which splits the ref at
 * its LAST colon — the hash never contains one.
 */
function transactionRouteId(hit: SearchHit): string {
  if (hit.id.includes(":") || !hit.id.toLowerCase().startsWith("0x")) return hit.id;
  const chainId = hit.chainId ?? hit.enrichment?.chains?.[0] ?? null;
  const chainToken = toStoredChainId(chainId) ?? chainId;
  return chainToken ? `${chainToken}:${hit.id}` : hit.id;
}

export function HeaderSearch(): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const provider = useSearchProvider();
  const { session } = useSession();
  const org = useCurrentOrg();

  const identity = React.useMemo<StorageIdentity>(
    () => ({
      app: APPLICATION_SLUG,
      customerId: org.customerId ?? null,
      orgId: org.orgId ?? null,
      tenantId: org.tenantId ?? null,
      userId: session?.user.id ?? null,
    }),
    [org.customerId, org.orgId, org.tenantId, session?.user.id],
  );

  // `SearchCommand` is a controlled dialog and doesn't register its own
  // shortcut, so the host owns ⌘K (mod = ⌘ on mac, ctrl elsewhere).
  React.useEffect(() => {
    const manager = new ShortcutManager();
    manager.register({
      combo: "mod+k",
      description: "Open search",
      allowInInput: true,
      handler: () => setOpen((v) => !v),
    });
    return manager.attach();
  }, []);

  const onSelectHit = React.useCallback(
    (hit: SearchHit) => {
      switch (hit.kind) {
        case "transaction":
          navigate({ to: "/transactions/$id", params: { id: transactionRouteId(hit) } });
          break;
        case "address":
          navigate({
            to: "/addresses/$id",
            params: { id: hit.id },
            search: hit.chainId ? { chain: hit.chainId } : {},
          });
          break;
        case "entity":
          navigate({ to: "/entities/$id", params: { id: hit.id } });
          break;
        case "group":
          navigate({ to: "/groups/$id", params: { id: hit.id } });
          break;
        case "label":
          navigate({ to: "/labels/$id", params: { id: hit.id } });
          break;
        case "tag":
          navigate({ to: "/tags/$id", params: { id: hit.id } });
          break;
        case "user":
          navigate({ to: "/team" });
          break;
        case "organization":
        case "tenant":
          navigate({ to: "/settings" });
          break;
        default:
          break;
      }
    },
    [navigate],
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 w-full max-w-md items-center gap-2 rounded-md border border-border bg-muted/40 px-3 text-sm text-muted-foreground transition-colors hover:bg-muted"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Search…</span>
        <kbd className="hidden rounded border border-border bg-background px-1.5 text-[10px] font-medium sm:inline">
          ⌘K
        </kbd>
      </button>
      <SearchCommand
        provider={provider}
        open={open}
        onOpenChange={setOpen}
        onSelectHit={onSelectHit}
        identity={identity}
      />
    </>
  );
}
